/**
 * useFunChallengesBattleSocket
 *
 * High-level façade over `useFunChallengesSocket`. Exposes intent-named
 * operations (`sendChallenge`, `acceptChallenge`, `sendMove`, …) and updates
 * the `funChallengesBattleStore` FSM optimistically where appropriate.
 *
 * Used by:
 *   - the lobby route (Phase 4) — `sendChallenge`
 *   - the global listener (Phase 4) — `acceptChallenge`, `rejectChallenge`,
 *     `cancelChallenge`
 *   - the battle screen (Phase 5) — `sendMove`, `forfeit`
 *   - the battle result modal (Phase 5) — `rematch`, `rematchWithSettings`
 */

import {useCallback, useMemo} from 'react';

import useFunChallengesBattleStore from '@/Store/funChallengesBattleStore';
import useFunChallengesSessionStore from '@/Store/funChallengesSessionStore';
import useLoggedInUserStore from '@/Store/loggedInUserstore';
import {
  BattleType,
  type ChallengeSettings,
} from '@/Screens/FunChallenges/types';

import {useFunChallengesSocket} from './useFunChallengesSocket';

export interface SendChallengeArgs {
  opponentId: string;
  opponentName?: string;
  battleType: BattleType;
  settings: ChallengeSettings;
}

export interface FunChallengesBattleSocketApi {
  ready: boolean;

  sendChallenge: (args: SendChallengeArgs) => boolean;
  acceptChallenge: (roomId: string, battleType?: BattleType) => boolean;
  rejectChallenge: (roomId: string, battleType?: BattleType) => boolean;
  cancelChallenge: () => boolean;

  startGame: (roomId: string, battleType?: BattleType) => boolean;
  sendMove: (correct: boolean, square: string) => boolean;
  forfeit: () => boolean;
  rejoinSession: () => boolean;

  /** Re-fires sendChallenge with the same settings as the most-recent battle. */
  rematch: () => boolean;
  /**
   * Resets `pendingChallenge` to null and lets the lobby reopen the settings
   * modal — the actual challenge is sent by the lobby once the user picks
   * new settings.
   */
  rematchWithSettings: () => void;
}

export function useFunChallengesBattleSocket(): FunChallengesBattleSocketApi {
  const {ready, send} = useFunChallengesSocket();

  // `loggedInUserstore.userId` is never populated; `userData.user_id` is.
  // The same fallback `useFunChallengesSocket` uses so identity is stable
  // across the feature.
  const userIdFromStore = useLoggedInUserStore(s => s.userId);
  const userIdFromUserData = useLoggedInUserStore(
    s => (s.userData && (s.userData.user_id ?? s.userData.pk)) || '',
  );
  const username = String(userIdFromUserData || userIdFromStore || '');
  // Display name shown to the opponent on their invite + result modals.
  // Falls back to userId so opponents never see an empty string.
  const displayName = useLoggedInUserStore(
    s =>
      s.userData?.username ||
      s.userData?.name ||
      s.userData?.first_name ||
      s.email ||
      '',
  );
  const challengerLabel = String(displayName || username || '').trim();
  const userIcon = useLoggedInUserStore(s => s.icon);

  const startChallenging = useFunChallengesBattleStore(s => s.startChallenging);
  const reset = useFunChallengesBattleStore(s => s.reset);
  const dismissIncomingChallenge = useFunChallengesBattleStore(
    s => s.dismissIncomingChallenge,
  );
  const recordMyMove = useFunChallengesBattleStore(s => s.recordMyMove);

  const sendChallenge = useCallback(
    ({opponentId, opponentName, battleType, settings}: SendChallengeArgs) => {
      if (!ready) return false;
      // Clear any stale room/session mirror — otherwise the global
      // listener's auto-start effect can read a prior session's room
      // (e.g. one where this user was an invitee) and fire fun_room_start
      // before the new opponent has even joined.
      useFunChallengesSessionStore.getState().clearAll();
      // Optimistically flip to 'challenging' so the "Waiting for opponent…"
      // modal shows immediately. If the actual send fails (schema not
      // registered, socket disconnected mid-send, etc.) we surface an
      // error and reset the FSM — otherwise the modal would hang
      // forever with no packet ever reaching the server.
      startChallenging({
        battleType,
        opponentId: String(opponentId),
        opponentName,
        settings,
      });
      const ok = send({
        type: 'fun_room_create',
        gameType: battleType,
        id: '',
        payload: {
          inviteeIds: [String(opponentId)],
          creatorName: challengerLabel,
          creatorAvatarUrl: typeof userIcon === 'string' ? userIcon : '',
          settings,
        },
      });
      if (!ok) {
        const battleStore = useFunChallengesBattleStore.getState();
        battleStore.setError({
          code: 'SEND_FAILED',
          message:
            "Couldn't send the challenge — please try again in a moment.",
        });
      }
      return ok;
    },
    [ready, send, startChallenging, challengerLabel, userIcon],
  );

  const acceptChallenge = useCallback(
    (roomId: string, battleType: BattleType = BattleType.SQUARE_TRAINER) => {
      // Optimistically close the "Incoming challenge" modal. The server will
      // either drive us to `in_battle` via `fun_session_start` or surface a
      // `fun_error`; in both cases the modal renders the right next state.
      useFunChallengesBattleStore.getState().markChallengeAccepted();
      return send({
        type: 'fun_room_join',
        gameType: battleType,
        id: roomId,
        payload: {
          // Display name (so the challenger sees a real name on their side),
          // not the numeric user id which the server already knows from auth.
          username: challengerLabel,
          avatarUrl: typeof userIcon === 'string' ? userIcon : '',
        },
      });
    },
    [send, challengerLabel, userIcon],
  );

  const rejectChallenge = useCallback(
    (roomId: string, battleType: BattleType = BattleType.SQUARE_TRAINER) => {
      const ok = send({
        type: 'fun_room_leave',
        gameType: battleType,
        id: roomId,
        payload: {},
      });
      // `dismissIncomingChallenge` restores the result modal if the invite
      // arrived while it was visible; otherwise it falls through to a
      // full reset. Avoids the "Reconnecting…" flash + 3.5s lobby bounce
      // we'd get from a plain reset() while still on BattleScreen.
      dismissIncomingChallenge();
      return ok;
    },
    [send, dismissIncomingChallenge],
  );

  const cancelChallenge = useCallback(() => {
    const pending = useFunChallengesBattleStore.getState().pendingChallenge;
    const roomId = pending?.roomId;
    const battleType = pending?.battleType ?? BattleType.SQUARE_TRAINER;
    if (!roomId) {
      reset();
      return true;
    }
    const ok = send({
      type: 'fun_room_leave',
      gameType: battleType,
      id: roomId,
      payload: {},
    });
    reset();
    return ok;
  }, [send, reset]);

  const startGame = useCallback(
    (roomId: string, battleType: BattleType = BattleType.SQUARE_TRAINER) => {
      return send({
        type: 'fun_room_start',
        gameType: battleType,
        id: roomId,
        payload: {},
      });
    },
    [send],
  );

  const sendMove = useCallback(
    (correct: boolean, square: string) => {
      const session = useFunChallengesBattleStore.getState().session;
      if (!session) return false;
      // Server is the single source of truth for score + chip state. We
      // used to call `recordMyMove(correct, square)` here for snappy local
      // feedback, but the server's `fun_move_broadcast` echo (which both
      // players receive) is the canonical event — and under rapid clicks
      // the optimistic path drifted from the echoed counts (chips +6 but
      // score +4 on the moving player; chips/score wrong on the opponent).
      // Removing the optimistic call: the moving player now sees their
      // chip/score appear when the echo arrives (~RTT). recordMyMove
      // itself is kept as a no-op for now; revisit later.
      return send({
        type: 'fun_move',
        gameType: session.battleType,
        id: session.battleId,
        payload: {
          correct: correct ? 1 : 0,
          square,
        },
      });
    },
    [send, recordMyMove],
  );

  const forfeit = useCallback(() => {
    const battle = useFunChallengesBattleStore.getState();
    const session = battle.session;
    if (!session) return false;
    if (battle.forfeitInFlight) return false; // dedupe double-press
    useFunChallengesBattleStore.getState().setForfeitInFlight(true);
    const ok = send({
      type: 'fun_forfeit',
      gameType: session.battleType,
      id: session.battleId,
      payload: {},
    });
    if (!ok) {
      // Best-effort recovery so a future click can retry.
      useFunChallengesBattleStore.getState().setForfeitInFlight(false);
    }
    return ok;
  }, [send]);

  const rejoinSession = useCallback(() => {
    return send({
      type: 'fun_session_rejoin',
      gameType: BattleType.SQUARE_TRAINER,
      id: '',
      payload: {},
    });
  }, [send]);

  const rematch = useCallback(() => {
    const result = useFunChallengesBattleStore.getState().endResult;
    const lastSession = useFunChallengesBattleStore.getState().session;
    const lastPending = useFunChallengesBattleStore.getState().pendingChallenge;
    if (!lastSession) return false;
    const opponentId =
      lastPending?.opponentId || lastSession.opponentId || '';
    if (!opponentId) return false;
    reset();
    return sendChallenge({
      opponentId,
      opponentName: lastPending?.opponentName ?? lastSession.opponentName,
      battleType: lastSession.battleType,
      settings: lastSession.settings,
    });
  }, [reset, sendChallenge]);

  const rematchWithSettings = useCallback(() => {
    // The lobby owns the settings modal. We:
    //   1. flag the lobby to auto-open the settings SlideModal on mount
    //   2. clear FSM so the lobby's "Send Challenge" button is enabled
    // The lobby will fire `sendChallenge` once the user picks new settings.
    useFunChallengesBattleStore.getState().requestOpenSettingsOnLobby();
    reset();
  }, [reset]);

  return useMemo<FunChallengesBattleSocketApi>(
    () => ({
      ready,
      sendChallenge,
      acceptChallenge,
      rejectChallenge,
      cancelChallenge,
      startGame,
      sendMove,
      forfeit,
      rejoinSession,
      rematch,
      rematchWithSettings,
    }),
    [
      ready,
      sendChallenge,
      acceptChallenge,
      rejectChallenge,
      cancelChallenge,
      startGame,
      sendMove,
      forfeit,
      rejoinSession,
      rematch,
      rematchWithSettings,
    ],
  );
}

export default useFunChallengesBattleSocket;
